import { X, Gift, Check } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { FREE_GIFTS } from '../data/freeGifts';
import { toast } from 'sonner';

export const FreeGiftPicker = () => {
  const { giftPickerOpen, setGiftPickerOpen, selectedGift, setSelectedGift } = useCart();

  const choose = (gift) => {
    setSelectedGift(gift);
    setGiftPickerOpen(false);
    toast.success(`Added a free ${gift.name} to your bag.`);
  };

  if (!giftPickerOpen) return null;

  return (
    <div
      data-testid="free-gift-picker"
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 md:p-6 bg-lum-text/50 backdrop-blur-sm animate-fade-up"
      onClick={() => setGiftPickerOpen(false)}
    >
      <div
        className="bg-lum-bg max-w-3xl w-full max-h-[92vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between p-6 md:p-8 border-b border-lum-border">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-lum-sale text-lum-bg text-[10px] uppercase tracking-[0.22em] mb-3">
              <Gift size={12} /> Free with your order
            </div>
            <h2 className="font-serif-display text-3xl md:text-4xl leading-tight">Pick your free gift.</h2>
            <p className="text-sm text-lum-text2 mt-2">A little something for the rest of you. Choose one — on us.</p>
          </div>
          <button
            data-testid="gift-picker-close"
            onClick={() => setGiftPickerOpen(false)}
            aria-label="close"
            className="text-lum-text2 hover:text-lum-text"
          >
            <X size={22} />
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6 md:p-8">
          {FREE_GIFTS.map((g) => {
            const isSelected = selectedGift?.id === g.id;
            return (
              <button
                key={g.id}
                data-testid={`gift-option-${g.id}`}
                onClick={() => choose(g)}
                className={`group text-left border transition-all ${isSelected ? 'border-lum-text bg-lum-bg2' : 'border-lum-border hover:border-lum-text'}`}
              >
                <div className={`aspect-square ${g.accent === 'green' ? 'accent-green-soft' : 'accent-orange-soft'} overflow-hidden relative`}>
                  <img src={g.image} alt={g.name} className="w-full h-full object-contain p-4 transition-transform duration-500 group-hover:scale-105" />
                  {isSelected && (
                    <span className="absolute top-3 right-3 w-7 h-7 rounded-full bg-lum-text text-lum-bg flex items-center justify-center">
                      <Check size={14} />
                    </span>
                  )}
                </div>
                <div className="p-5">
                  <p className="overline mb-2 text-lum-sale">Free · $0</p>
                  <h3 className="font-serif-display text-2xl leading-tight">{g.name}</h3>
                  <p className="text-sm text-lum-text2 mt-2">{g.tagline}</p>
                  <p className="text-xs text-lum-muted mt-3 leading-relaxed">{g.description}</p>
                </div>
                <div className={`px-5 py-3 text-xs uppercase tracking-[0.22em] text-center ${isSelected ? 'bg-lum-text text-lum-bg' : 'bg-lum-bg2 text-lum-text group-hover:bg-lum-text group-hover:text-lum-bg'}`}>
                  {isSelected ? 'Selected' : 'Choose this gift'}
                </div>
              </button>
            );
          })}
        </div>

        <div className="px-6 md:px-8 pb-6 md:pb-8 text-center">
          <button onClick={() => setGiftPickerOpen(false)} className="btn-ghost">Skip for now</button>
        </div>
      </div>
    </div>
  );
};
